import { usePrimaryCopilotSession } from '@customerio/hydra-copilot/react';
import { useEffect, useRef, useState, useCallback } from 'react';
import type { CopilotSession, CopilotStatefulMessage } from '@customerio/hydra-copilot';
import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';
import { useIsFullScreenAssistantPage } from '@/features/copilot/hooks';
import clsx from 'clsx';
import * as styles from './ai-shimmer.css';

const LINGER_MS = 1500;
const FADE_MS = 600;
const emptyMessages = atom<CopilotStatefulMessage[]>([]);

function useIsBashRunning() {
  const session = usePrimaryCopilotSession();
  const messages = useStore(session != null ? (session as CopilotSession).__statefulMessages : emptyMessages);

  return messages.some(
    (m) =>
      m.type === 'tool_call' &&
      'tool_calls' in m &&
      m.tool_calls.some(
        // Only show shimmer for post-approval execution, not while waiting for user to approve
        (tc) => tc.name === 'bash' && tc.status === 'executing' && tc.was_approved === true,
      ),
  );
}

export function AIShimmer() {
  const isFullScreenPage = useIsFullScreenAssistantPage();
  const bashRunning = useIsBashRunning();
  const active = bashRunning && !isFullScreenPage;

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const lingerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const rafRef2 = useRef<number | null>(null);

  const clearAllTimers = useCallback(() => {
    if (lingerRef.current != null) {
      clearTimeout(lingerRef.current);
      lingerRef.current = null;
    }
    if (unmountRef.current != null) {
      clearTimeout(unmountRef.current);
      unmountRef.current = null;
    }
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (rafRef2.current != null) {
      cancelAnimationFrame(rafRef2.current);
      rafRef2.current = null;
    }
  }, []);

  useEffect(() => {
    if (active) {
      clearAllTimers();
      setMounted(true);
      // Double rAF to ensure the DOM has painted at opacity 0 before transitioning to 1
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        rafRef2.current = requestAnimationFrame(() => {
          rafRef2.current = null;
          setVisible(true);
        });
      });
    } else if (mounted) {
      // Linger, then fade out, then unmount
      lingerRef.current ??= setTimeout(() => {
        lingerRef.current = null;
        setVisible(false);
        // Wait for the CSS fade-out transition to finish before unmounting
        unmountRef.current = setTimeout(() => {
          unmountRef.current = null;
          setMounted(false);
        }, FADE_MS);
      }, LINGER_MS);
    }
  }, [active, mounted, clearAllTimers]);

  // Cleanup on unmount only
  useEffect(() => {
    return clearAllTimers;
  }, [clearAllTimers]);

  if (!mounted) {
    return null;
  }

  return (
    <div className={clsx(styles.shimmer, visible && styles.shimmerVisible)}>
      <div className={styles.clip}>
        <div className={styles.orb1} />
        <div className={styles.orb2} />
      </div>
    </div>
  );
}
